import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { IWedding_RSVP } from '@bridebook/models/source/models/Weddings.types';
import { Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import {
  IEventFormFields,
  INameFormFields,
  IQuestionsFormFields,
} from '@bridebook/web/lib/rsvp/types';
import {
  getGuestRsvpQuestionValidator,
  validateGuestRsvpEventAttendance,
} from '@bridebook/web/lib/rsvp/validation';

interface UseRsvpPreviewFormsParams {
  visibleEvents: IWeddingEvent[];
  weddingRsvp: IWedding_RSVP | null | undefined;
  websiteId: string;
}

export const useRsvpPreviewForms = ({
  visibleEvents,
  weddingRsvp,
  websiteId,
}: UseRsvpPreviewFormsParams) => {
  const { t } = useTranslation('weddingWebsite');

  const nameForm = useForm<INameFormFields>({
    defaultValues: {
      fullName: t('rsvp.preview.guestName'),
    },
  });

  const eventsForm = useForm<IEventFormFields>();

  const prevEventIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const currentIds = visibleEvents.map((event) => event.id);

    // Unregister removed events
    prevEventIdsRef.current
      .filter((id) => !currentIds.includes(id))
      .forEach((id) => eventsForm.unregister(id));

    // Register current events
    visibleEvents.forEach((event) => {
      eventsForm.register(event.id, {
        value: Rsvp.PENDING,
        validate: validateGuestRsvpEventAttendance,
        required: true,
      });
    });

    prevEventIdsRef.current = currentIds;
    eventsForm.trigger();
  }, [eventsForm, visibleEvents]);

  const questionsForm = useForm<IQuestionsFormFields>({ mode: 'onChange' });

  const eventsSubscriptionRef = useRef<ReturnType<typeof eventsForm.watch> | null>(null);

  const updateQuestionRegistrations = useCallback(() => {
    if (!weddingRsvp?.questions) return;
    const eventResponses = eventsForm.getValues();

    weddingRsvp.questions.forEach((question) => {
      const shouldRegister = question.events.some(
        (eventId) => eventResponses[eventId] === Rsvp.YES,
      );

      if (shouldRegister) {
        questionsForm.register(question.id, {
          validate: getGuestRsvpQuestionValidator(question, t),
          value: '',
        });
      } else {
        questionsForm.unregister(question.id);
      }
    });
  }, [weddingRsvp, eventsForm, questionsForm, t]);

  useEffect(() => {
    if (!weddingRsvp?.questions) return;

    updateQuestionRegistrations();

    questionsForm.register('note', { value: '' });

    if (eventsSubscriptionRef.current) {
      eventsSubscriptionRef.current.unsubscribe();
    }

    // Re-evaluate which questions to show whenever event RSVP answers change
    // (questions are tied to specific events, so toggling an event on/off must
    // register/unregister the associated questions).
    eventsSubscriptionRef.current = eventsForm.watch(updateQuestionRegistrations);

    return () => {
      if (eventsSubscriptionRef.current) {
        eventsSubscriptionRef.current.unsubscribe();
        eventsSubscriptionRef.current = null;
      }
    };
  }, [weddingRsvp, updateQuestionRegistrations, eventsForm, questionsForm]);

  const handleSubmit = useCallback(
    async () => ({
      response: {
        status: 'ok' as const,
        wedding: { id: websiteId },
      },
    }),
    [websiteId],
  );

  return { nameForm, eventsForm, questionsForm, handleSubmit };
};
